// Alerts Page Component - Red flags and system alerts management

import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardBody, Button, StatusBadge } from '../components/common';
import { formatDate, formatRelativeTime } from '../utils/dateFormatters';
import { adminService, type RedFlag } from '../services/admin';
import { WarningIcon, CheckIcon, EyeIcon, RefreshIcon, XIcon } from '../components/icons';
import { AcceptSageDialog } from '../components/alerts/AcceptSageDialog';
import styles from './AlertsPage.module.css';

// Flags whose order can be rewritten to match Sage (Accept-Sage-as-final).
const ACCEPT_SAGE_FLAG_TYPE = 'sage_confirmation_mismatch';

// Severity badge component
function SeverityBadge({ severity }: { severity: string }) {
  const variant = severity === 'critical' ? 'error'
    : severity === 'high' ? 'warning'
    : 'info';
  return (
    <StatusBadge variant={variant}>
      {severity.toUpperCase()}
    </StatusBadge>
  );
}

// Status badge component
function FlagStatusBadge({ status }: { status: string }) {
  const variant = status === 'active' ? 'error'
    : status === 'acknowledged' ? 'warning'
    : status === 'resolved' ? 'success'
    : 'info';
  return (
    <StatusBadge variant={variant}>
      {status}
    </StatusBadge>
  );
}

// Flag type badge
function FlagTypeBadge({ type }: { type: string }) {
  const label = type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  return (
    <span className={styles.flagTypeBadge}>
      {label}
    </span>
  );
}

// Status filter options
type StatusFilter = 'all' | 'active' | 'acknowledged' | 'resolved' | 'ignored';

export function AlertsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [flags, setFlags] = useState<RedFlag[]>([]);
  const [allFlags, setAllFlags] = useState<RedFlag[]>([]); // For counts
  const [loading, setLoading] = useState(true);
  const [, setTotal] = useState(0);
  const [selectedFlag, setSelectedFlag] = useState<RedFlag | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(
    (searchParams.get('status') as StatusFilter) || 'all'
  );
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showIgnoreDialog, setShowIgnoreDialog] = useState<string | null>(null);
  const [acceptSageFlagId, setAcceptSageFlagId] = useState<string | null>(null);

  // Fetch all flags for counts (include resolved)
  const fetchAllFlags = useCallback(async () => {
    try {
      const response = await adminService.getRedFlags({
        include_resolved: true,
        limit: 200, // API limit is 200
      });
      setAllFlags(response.flags);
    } catch (error) {
      console.error('Failed to fetch all flags for counts:', error);
    }
  }, []);

  const fetchFlags = useCallback(async () => {
    setLoading(true);
    try {
      const params: {
        status?: 'active' | 'acknowledged' | 'resolved' | 'ignored';
        include_acknowledged?: boolean;
        include_resolved?: boolean;
        limit: number;
      } = {
        limit: 100,
      };

      if (statusFilter === 'all') {
        // Show active and acknowledged in the default tab. The API
        // default is now active-only, so we must opt in explicitly.
        params.include_acknowledged = true;
      } else {
        // Filter by specific status
        params.status = statusFilter;
      }

      const response = await adminService.getRedFlags(params);
      setFlags(response.flags);
      setTotal(response.total);

      // If a flag ID is in the URL, select it
      const flagId = searchParams.get('id');
      if (flagId) {
        const flag = response.flags.find(f => f.id === flagId);
        if (flag) {
          setSelectedFlag(flag);
        } else {
          // Flag not in list, fetch it directly
          try {
            const detailFlag = await adminService.getRedFlag(flagId);
            setSelectedFlag(detailFlag);
          } catch {
            // Flag not found, clear the param
            searchParams.delete('id');
            setSearchParams(searchParams);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch red flags:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchParams, setSearchParams]);

  useEffect(() => {
    fetchFlags();
    fetchAllFlags();
  }, [fetchFlags, fetchAllFlags]);

  const handleSelectFlag = (flag: RedFlag) => {
    setSelectedFlag(flag);
    searchParams.set('id', flag.id);
    setSearchParams(searchParams);
  };

  const handleAcknowledge = async (flagId: string) => {
    setActionLoading(flagId);
    try {
      await adminService.acknowledgeRedFlag(flagId);
      await fetchFlags();
    } catch (error) {
      console.error('Failed to acknowledge flag:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleResolve = async (flagId: string) => {
    setActionLoading(flagId);
    try {
      await adminService.resolveRedFlag(flagId);
      await fetchFlags();
      setSelectedFlag(null);
    } catch (error) {
      console.error('Failed to resolve flag:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleIgnore = async (flagId: string) => {
    setActionLoading(flagId);
    try {
      await adminService.ignoreRedFlag(flagId);
      await fetchFlags();
      await fetchAllFlags();
      setShowIgnoreDialog(null);
      setSelectedFlag(null);
    } catch (error) {
      console.error('Failed to ignore flag:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleAcceptedSage = async () => {
    await fetchFlags();
    await fetchAllFlags();
    setSelectedFlag(null);
  };

  // Calculate counts from all flags
  const activeCount = allFlags.filter(f => f.status === 'active').length;
  const acknowledgedCount = allFlags.filter(f => f.status === 'acknowledged').length;
  const resolvedCount = allFlags.filter(f => f.status === 'resolved').length;
  const ignoredCount = allFlags.filter(f => f.status === 'ignored').length;

  const handleStatusFilterChange = (status: StatusFilter) => {
    setStatusFilter(status);
    if (status === 'all') {
      searchParams.delete('status');
    } else {
      searchParams.set('status', status);
    }
    setSearchParams(searchParams);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>
            <WarningIcon className={styles.titleIcon} />
            System Alerts
          </h1>
        </div>
        <div className={styles.headerActions}>
          <div className={styles.statusTabs}>
            <button
              className={`${styles.statusTab} ${statusFilter === 'all' ? styles.active : ''}`}
              onClick={() => handleStatusFilterChange('all')}
            >
              Open
              <span className={styles.statusTabCount}>{activeCount + acknowledgedCount}</span>
            </button>
            <button
              className={`${styles.statusTab} ${styles.activeStatus} ${statusFilter === 'active' ? styles.active : ''}`}
              onClick={() => handleStatusFilterChange('active')}
            >
              Active
              <span className={styles.statusTabCount}>{activeCount}</span>
            </button>
            <button
              className={`${styles.statusTab} ${styles.acknowledgedStatus} ${statusFilter === 'acknowledged' ? styles.active : ''}`}
              onClick={() => handleStatusFilterChange('acknowledged')}
            >
              Acknowledged
              <span className={styles.statusTabCount}>{acknowledgedCount}</span>
            </button>
            <button
              className={`${styles.statusTab} ${styles.resolvedStatus} ${statusFilter === 'resolved' ? styles.active : ''}`}
              onClick={() => handleStatusFilterChange('resolved')}
            >
              Resolved
              <span className={styles.statusTabCount}>{resolvedCount}</span>
            </button>
            <button
              className={`${styles.statusTab} ${styles.ignoredStatus} ${statusFilter === 'ignored' ? styles.active : ''}`}
              onClick={() => handleStatusFilterChange('ignored')}
            >
              Ignored
              <span className={styles.statusTabCount}>{ignoredCount}</span>
            </button>
          </div>
          <Button variant="outline" onClick={() => { fetchFlags(); fetchAllFlags(); }} disabled={loading}>
            <RefreshIcon />
            Refresh
          </Button>
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.flagList}>
          {loading ? (
            <div className={styles.loading}>Loading alerts...</div>
          ) : flags.length === 0 ? (
            <div className={styles.empty}>
              <CheckIcon className={styles.emptyIcon} />
              <p>No {statusFilter === 'all' ? 'open' : statusFilter} alerts</p>
              {statusFilter === 'all' && <p className={styles.emptyHint}>Use the tabs above to view resolved or ignored alerts</p>}
            </div>
          ) : (
            flags.map((flag) => (
              <div
                key={flag.id}
                className={`${styles.flagCard} ${selectedFlag?.id === flag.id ? styles.selected : ''} ${styles[flag.severity]}`}
                onClick={() => handleSelectFlag(flag)}
              >
                <div className={styles.flagCardHeader}>
                  <div className={styles.flagBadges}>
                    <SeverityBadge severity={flag.severity} />
                    <FlagStatusBadge status={flag.status} />
                  </div>
                  <span className={styles.flagTime}>{formatRelativeTime(flag.created_at)}</span>
                </div>
                <h3 className={styles.flagTitle}>{flag.title}</h3>
                <p className={styles.flagMessage}>{flag.message.substring(0, 120)}...</p>
                <div className={styles.flagCardFooter}>
                  <FlagTypeBadge type={flag.flag_type} />
                  {(flag.status === 'active' || flag.status === 'acknowledged') && (
                    <div className={styles.flagCardActions} onClick={(e) => e.stopPropagation()}>
                      {flag.status === 'active' && (
                        <button
                          className={styles.cardActionButton}
                          onClick={() => handleAcknowledge(flag.id)}
                          disabled={actionLoading === flag.id}
                          title="Acknowledge"
                        >
                          <EyeIcon />
                        </button>
                      )}
                      <button
                        className={`${styles.cardActionButton} ${styles.resolve}`}
                        onClick={() => handleResolve(flag.id)}
                        disabled={actionLoading === flag.id}
                        title="Resolve"
                      >
                        <CheckIcon />
                      </button>
                      <button
                        className={styles.cardActionButton}
                        onClick={() => setShowIgnoreDialog(flag.id)}
                        disabled={actionLoading === flag.id}
                        title="Ignore"
                      >
                        <XIcon />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        <div className={styles.detailPanel}>
          {selectedFlag ? (
            <Card className={styles.detailCard}>
              <CardHeader>
                <div className={styles.detailHeader}>
                  <div className={styles.detailBadges}>
                    <SeverityBadge severity={selectedFlag.severity} />
                    <FlagStatusBadge status={selectedFlag.status} />
                    <FlagTypeBadge type={selectedFlag.flag_type} />
                  </div>
                  <button
                    className={styles.closeButton}
                    onClick={() => {
                      setSelectedFlag(null);
                      searchParams.delete('id');
                      setSearchParams(searchParams);
                    }}
                  >
                    <XIcon />
                  </button>
                </div>
              </CardHeader>
              <CardBody>
                <h2 className={styles.detailTitle}>{selectedFlag.title}</h2>
                <p className={styles.detailMessage}>{selectedFlag.message}</p>

                <div className={styles.detailMeta}>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Created</span>
                    <span className={styles.metaValue}>{formatDate(selectedFlag.created_at)}</span>
                  </div>
                  {selectedFlag.acknowledged_at && (
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>Acknowledged</span>
                      <span className={styles.metaValue}>
                        {formatDate(selectedFlag.acknowledged_at)}
                        {selectedFlag.acknowledged_by && ` by ${selectedFlag.acknowledged_by}`}
                      </span>
                    </div>
                  )}
                  {selectedFlag.resolved_at && (
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>Resolved</span>
                      <span className={styles.metaValue}>
                        {formatDate(selectedFlag.resolved_at)}
                        {selectedFlag.resolved_by && ` by ${selectedFlag.resolved_by}`}
                      </span>
                    </div>
                  )}
                  {selectedFlag.resolution_notes && (
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>Resolution Notes</span>
                      <span className={styles.metaValue}>{selectedFlag.resolution_notes}</span>
                    </div>
                  )}
                  {selectedFlag.related_document_id && (
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>Related Document</span>
                      <span className={styles.metaValue}>
                        <button
                          className={styles.linkButton}
                          onClick={() => navigate(`/rag?doc=${selectedFlag.related_document_id}`)}
                        >
                          {selectedFlag.related_document_id}
                        </button>
                      </span>
                    </div>
                  )}
                  {selectedFlag.related_ftp_path && (
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>FTP Path</span>
                      <span className={styles.metaValue}>{selectedFlag.related_ftp_path}</span>
                    </div>
                  )}
                </div>

                {selectedFlag.details && Object.keys(selectedFlag.details).length > 0 && (
                  <div className={styles.detailSection}>
                    <h4>Details</h4>
                    <pre className={styles.detailJson}>
                      {JSON.stringify(selectedFlag.details, null, 2)}
                    </pre>
                  </div>
                )}

                {(selectedFlag.status === 'active' || selectedFlag.status === 'acknowledged') && (
                  <div className={styles.detailActions}>
                    {selectedFlag.status === 'active' && (
                      <Button
                        variant="outline"
                        onClick={() => handleAcknowledge(selectedFlag.id)}
                        disabled={actionLoading === selectedFlag.id}
                      >
                        <EyeIcon />
                        Acknowledge
                      </Button>
                    )}
                    <Button
                      variant="primary"
                      onClick={() => handleResolve(selectedFlag.id)}
                      disabled={actionLoading === selectedFlag.id}
                    >
                      <CheckIcon />
                      Resolve
                    </Button>
                    {selectedFlag.flag_type === ACCEPT_SAGE_FLAG_TYPE && (
                      <Button
                        variant="outline"
                        onClick={() => setAcceptSageFlagId(selectedFlag.id)}
                        disabled={actionLoading === selectedFlag.id}
                        title="Rewrite our order to match the Sage sales order and resolve this flag"
                      >
                        Accept Sage values
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={() => setShowIgnoreDialog(selectedFlag.id)}
                      disabled={actionLoading === selectedFlag.id}
                    >
                      Ignore
                    </Button>
                  </div>
                )}
              </CardBody>
            </Card>
          ) : (
            <div className={styles.noSelection}>
              <EyeIcon className={styles.noSelectionIcon} />
              <p>Select an alert to view details</p>
            </div>
          )}
        </div>
      </div>

      <AcceptSageDialog
        flagId={acceptSageFlagId}
        onClose={() => setAcceptSageFlagId(null)}
        onAccepted={handleAcceptedSage}
      />

      {/* Ignore Confirmation Dialog */}
      {showIgnoreDialog && (
        <div className={styles.dialogOverlay} onClick={() => setShowIgnoreDialog(null)}>
          <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
            <div className={styles.dialogHeader}>
              <div className={styles.dialogIcon}>
                <XIcon />
              </div>
              <h3>Ignore Alert</h3>
            </div>
            <p>Are you sure you want to ignore this alert? It will be hidden from your active alerts list.</p>
            <div className={styles.dialogActions}>
              <Button variant="outline" onClick={() => setShowIgnoreDialog(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => handleIgnore(showIgnoreDialog)}
                disabled={actionLoading === showIgnoreDialog}
              >
                {actionLoading === showIgnoreDialog ? 'Ignoring...' : 'Ignore Alert'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
